// A server refusal, said in the reader's language.
//
// The server writes its refusals in English and gives each a stable code; the code
// is the contract, and this is where the screens keep their side of it. The rules,
// in order, are in the plan that introduced this file and in the tests beside it —
// the one worth repeating here is the second: an English reader gets the server's
// own sentence, unchanged, because it is often more specific than any phrase kept
// here, and because nothing an English reader sees changed when the other two
// languages arrived.
import { ApiError } from './api'
import { CLIENT_FAULTS } from './phrases/errors'
import { hasPhrase, nameIn, placeholdersOf, t, type StaffKey, type StaffLanguage, type Vars } from './staffText'
import type { ProfileProblem } from './types'

export function explain(err: unknown, lang: StaffLanguage, otherwise: StaffKey): string {
  if (!(err instanceof ApiError)) return t(otherwise, lang)
  if (lang === 'en') return err.message

  const args: Vars = { ...(err.args ?? {}), id: err.correlationId ?? '—' }
  // A module refusal sends the module's English label as {module} and its name
  // as {moduleKey}. The screens already name each module in every language, so
  // the reader's own name for it is used; the label stays when this build does
  // not know the key, which is better than a raw identifier.
  const moduleKey = err.args?.moduleKey
  if (moduleKey && hasPhrase(`org.br.module.${moduleKey}`)) {
    args.module = t(`org.br.module.${moduleKey}` as StaffKey, lang)
  }
  if (CLIENT_FAULTS.includes(err.code)) return t('errors.clientFault', lang, args)
  if (err.problems?.length && hasPhrase(`errors.${err.code}`)) {
    const coded = codedProblems(err.code, err.problems, lang)
    if (coded) return coded
  }

  // A code with words is said in words even when its status is a 5xx: a 503 such
  // as NOT_PROVISIONED is a sentence the person can act on, and problem.Write has
  // already replaced its English detail with the generic one on the way out.
  // (DIRECTORY_UNREACHABLE is not an example of this — the server sends it as 409
  // or 422, never a 5xx — but the rule below still has to hold for whatever does
  // reach this branch at 500 and above.)
  const candidates = err.reason ? [`errors.${err.code}.${err.reason}`, `errors.${err.code}`] : [`errors.${err.code}`]
  for (const key of candidates) {
    if (!hasPhrase(key)) continue
    // A sentence that needs a name the server did not send is worse than the
    // server's English: "{desk}" on a teller's screen is a fault they will report.
    if (!placeholdersOf(key).every((name) => name in args)) return err.message
    return t(key, lang, args)
  }
  if (err.status >= 500) return t('errors.INTERNAL', lang, args)
  return err.message
}

/**
 * A refusal whose problems each carry a code of their own (CONFIG_WOULD_STRAND:
 * one per person a publish would strand), said one sentence each as
 * errors.<CODE>.<problem code>. English reads the server's sentences. A problem
 * this build has no words for keeps its English; '' when there are no problems.
 */
export function codedProblems(code: string, problems: readonly ProfileProblem[] | undefined, lang: StaffLanguage): string {
  if (!problems?.length) return ''
  return problems
    .map((p) => {
      const key = `errors.${code}.${p.code}`
      if (lang === 'en' || !hasPhrase(key)) return p.message
      const vars: Vars = {}
      for (const [name, value] of Object.entries(p.args ?? {})) {
        if (typeof value === 'number' || typeof value === 'string') vars[name] = value
      }
      // A queue is named in the reader's language when the server sent its names.
      const names = p.args?.queueName
      if (names && typeof names === 'object' && typeof p.args?.queueCode === 'string') {
        vars.queue = `${nameIn(names as Record<string, string>, lang, p.args.queueCode)} (${p.args.queueCode})`
      }
      if (!placeholdersOf(key).every((name) => name in vars)) return p.message
      return t(key, lang, vars)
    })
    .join(' ')
}

/** explain(), followed by each blocking problem the refusal carries that its sentence does not already say. */
export function refusedWith(err: unknown, lang: StaffLanguage, otherwise: StaffKey): string {
  const head = explain(err, lang, otherwise)
  if (!(err instanceof ApiError)) return head
  const lines = codedProblems(err.code, err.problems?.filter((p) => !p.warning), lang)
  return lines && !head.includes(lines) ? `${head} ${lines}` : head
}
